import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { API_URL } from './config';
import GoalsDisplay from './GoalsDisplay';
import './Equity.css';

function Equity({ activeSubmenu = 'equity-table' }) {
  const [equityData, setEquityData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [depositsData, setDepositsData] = useState([]);
  const [depositsLoading, setDepositsLoading] = useState(false);
  const [depositsError, setDepositsError] = useState(null);
  const [depositsFilterDate, setDepositsFilterDate] = useState('');
  const [depositsFilterType, setDepositsFilterType] = useState('all');
  const [depositsHideInternal, setDepositsHideInternal] = useState(false);
  const [depositsSortConfig, setDepositsSortConfig] = useState({ key: 'date', direction: 'desc' });
  const [selectedAccount, setSelectedAccount] = useState('all');
  const [selectedBroker, setSelectedBroker] = useState('all');
  const [accounts, setAccounts] = useState([]);
  const [userSettings, setUserSettings] = useState(null);
  const [groupBy, setGroupBy] = useState('day'); // 'day', 'week', 'month'
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'desc' });
  const [showDiffColumns, setShowDiffColumns] = useState(true);
  const [displayCurrency, setDisplayCurrency] = useState('EUR');
  const [eurToUsdRate, setEurToUsdRate] = useState(null);
  
  // Load P&L mode preference from localStorage
  const loadPnLModePreference = () => {
    try {
      const saved = localStorage.getItem('goalsDisplay_preferences');
      if (saved) {
        const prefs = JSON.parse(saved);
        return prefs.pnlMode || 'equity';
      }
    } catch (e) {
      console.error('Error loading P&L mode preference:', e);
    }
    return 'equity'; // Default
  };
  
  const [pnlMode, setPnlMode] = useState(loadPnLModePreference()); // 'equity' or 'realized'
  
  // Save P&L mode preference when it changes
  const handlePnLModeChange = (newMode) => {
    setPnlMode(newMode);
    try {
      const saved = localStorage.getItem('goalsDisplay_preferences');
      const prefs = saved ? JSON.parse(saved) : {};
      prefs.pnlMode = newMode;
      localStorage.setItem('goalsDisplay_preferences', JSON.stringify(prefs));
    } catch (e) {
      console.error('Error saving P&L mode preference:', e);
    }
  };

  useEffect(() => {
    fetchAccounts();
    fetchUserSettings();
    fetchFxRate();
  }, []);

  const fetchFxRate = async () => {
    try {
      const res = await axios.get(`${API_URL}/portfolio/equity/fx-rate/`);
      if (res.data.eur_to_usd) setEurToUsdRate(res.data.eur_to_usd);
    } catch (e) {
      console.error('Could not fetch FX rate', e);
    }
  };

  const fetchUserSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/portfolio/user-settings/`);
      // Handle both list and single object responses
      let settings = null;
      if (response.data.results && Array.isArray(response.data.results)) {
        settings = response.data.results[0] || null;
      } else if (Array.isArray(response.data)) {
        settings = response.data[0] || null;
      } else {
        settings = response.data || null;
      }
      setUserSettings(settings);
    } catch (err) {
      console.error('Error fetching user settings:', err);
      setUserSettings(false); // Set to false if error (no settings found)
    }
  };

  useEffect(() => {
    fetchEquityData();
  }, [selectedAccount, selectedBroker]);

  useEffect(() => {
    if (activeSubmenu !== 'deposits') return;
    const fetchDepositsData = async () => {
      try {
        setDepositsLoading(true);
        setDepositsError(null);

        // Always fetch all accounts so we can classify internal transfers (opposite-signed movement).
        // Real, itemized IBKR Cash Transactions (unique transaction_id, exact date) - not the
        // old ChangeInNAV rolling-window summary, which could report the same withdrawal
        // repeatedly for as long as it stayed inside the window.
        let url = `${API_URL}/portfolio/cash-transactions/?type=${encodeURIComponent('Deposits/Withdrawals')}&page_size=all`;

        const response = await axios.get(url);
        const data = response.data.results || response.data;
        setDepositsData(Array.isArray(data) ? data : []);
      } catch (err) {
        setDepositsError(err.response?.data?.detail || err.response?.data?.error || err.message || 'Error fetching deposits');
        setDepositsData([]);
      } finally {
        setDepositsLoading(false);
      }
    };

    fetchDepositsData();
  }, [activeSubmenu]);

  const fetchAccounts = async () => {
    try {
      const response = await axios.get(`${API_URL}/portfolio/accounts/?page_size=all`);
      const accountsData = response.data.results || response.data;
      setAccounts(Array.isArray(accountsData) ? accountsData : []);
    } catch (err) {
      console.error('Error fetching accounts:', err);
    }
  };

  const fetchEquityData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      let url = `${API_URL}/portfolio/equity/`;
      const params = [];
      if (selectedBroker && selectedBroker !== 'all') {
        params.push(`broker=${selectedBroker}`);
      }
      if (selectedAccount && selectedAccount !== 'all') {
        const account = accounts.find(acc =>
          acc.id === parseInt(selectedAccount) || acc.broker_account_id === selectedAccount
        );
        if (account) {
          params.push(`account_id=${account.broker_account_id}`);
        }
      }
      if (params.length) url += `?${params.join('&')}`;
      
      const response = await axios.get(url);
      const data = response.data.results || response.data;
      const dataArray = Array.isArray(data) ? data : [];
      
      // Sort by date (newest first) - IBKR date format: DD/MM/YY
      const sortedData = dataArray.sort((a, b) => {
        const parseDate = (dateStr) => {
          if (!dateStr) return 0;
          const parts = dateStr.split('/');
          if (parts.length === 3) {
            const day = parseInt(parts[0], 10);
            const month = parseInt(parts[1], 10);
            const year = 2000 + parseInt(parts[2], 10);
            return new Date(year, month - 1, day).getTime();
          }
          return 0;
        };
        return parseDate(b.report_date) - parseDate(a.report_date);
      });
      
      setEquityData(sortedData);
    } catch (err) {
      if (err.response && err.response.status === 401) {
        setError('Session expired. Please log in again.');
      } else {
        setError('Error fetching equity data: ' + err.message);
      }
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Parse IBKR date format (DD/MM/YY) to Date object
  const parseIBKRDate = (dateStr) => {
    if (!dateStr) return null;
    const parts = dateStr.split('/');
    if (parts.length === 3) {
      const day = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10);
      const year = 2000 + parseInt(parts[2], 10);
      return new Date(year, month - 1, day);
    }
    return null;
  };

  // Display-only: CashTransaction.date arrives as ISO 'YYYY-MM-DD' - render as
  // unambiguous Dutch 'DD-MM-YYYY' (still day-first, matching the rest of the app).
  const formatDutchDate = (isoDateStr) => {
    if (!isoDateStr) return '—';
    const [y, m, d] = isoDateStr.split('-');
    if (!y || !m || !d) return isoDateStr;
    return `${d}-${m}-${y}`;
  };

  const startOfLocalDay = (d) => {
    if (!d) return null;
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  };

  const getTransferInfo = (row, allNavRows) => {
    const amount = row.amount != null ? parseFloat(row.amount) : 0;
    const tol = 0.01;
    const other = (allNavRows || []).find((r) => {
      if (!r || r.id === row.id) return false;
      if (r.date !== row.date) return false;
      if (r.account_id === row.account_id) return false;
      const otherAmt = r.amount != null ? parseFloat(r.amount) : 0;
      return Math.abs(otherAmt + amount) < tol;
    });
    const otherLabel = other ? (other.account_display_name || other.account_id || 'other account') : null;
    if (amount > 0) {
      return other
        ? { kind: 'internal_in', label: `Internal transfer from ${otherLabel}` }
        : { kind: 'bank_deposit', label: 'Deposited from bank account' };
    }
    return other
      ? { kind: 'internal_out', label: `Internal transfer to ${otherLabel}` }
      : { kind: 'bank_withdrawal', label: 'Withdrawn to bank account' };
  };

  const rowMatchesDepositsDateFilter = (row, filterYmd) => {
    if (!filterYmd) return true;
    // row.date is a real, exact ISO date (YYYY-MM-DD) from CashTransaction - a plain
    // string comparison is correct and unambiguous, no range/approximation needed.
    return row.date === filterYmd;
  };

  const rowMatchesDepositsAccount = (row) => {
    if (!selectedAccount || selectedAccount === 'all') return true;
    const acc = accounts.find((a) => String(a.id) === String(selectedAccount));
    if (acc && acc.broker_account_id) {
      return row.account_id === acc.broker_account_id;
    }
    return row.account_id === selectedAccount || String(row.account) === String(selectedAccount);
  };

  const depositsBaseRows = useMemo(() => {
    if (activeSubmenu !== 'deposits') return [];
    const nav = depositsData || [];
    const nonzero = nav.filter(
      (r) => r && r.amount != null && Math.abs(parseFloat(r.amount)) > 0
    );
    return nonzero
      .map((row) => {
        const { kind, label } = getTransferInfo(row, nav);
        return { row, kind, label };
      })
      .filter(({ row }) => rowMatchesDepositsAccount(row))
      .filter(({ row }) => rowMatchesDepositsDateFilter(row, depositsFilterDate));
  }, [
    activeSubmenu,
    depositsData,
    selectedAccount,
    accounts,
    depositsFilterDate,
  ]);

  const depositsTableRows = useMemo(() => {
    if (activeSubmenu !== 'deposits') return [];
    return (depositsBaseRows || [])
      .filter(({ kind }) => {
        if (!depositsFilterType || depositsFilterType === 'all') return true;
        if (depositsFilterType === 'internal') {
          return kind === 'internal_in' || kind === 'internal_out';
        }
        return kind === depositsFilterType;
      })
      .filter(({ kind }) => {
        if (!depositsHideInternal) return true;
        return kind !== 'internal_in' && kind !== 'internal_out';
      });
  }, [activeSubmenu, depositsBaseRows, depositsFilterType, depositsHideInternal]);

  const parseNumber = (v) => {
    if (v === null || v === undefined || v === '') return null;
    const n = typeof v === 'number' ? v : parseFloat(String(v).replace(',', '.'));
    return Number.isFinite(n) ? n : null;
  };

  const depositsSummary = useMemo(() => {
    if (activeSubmenu !== 'deposits') {
      return {
        bankDeposited: 0,
        bankWithdrawn: 0,
        bankNet: 0,
        internalIn: 0,
        internalOut: 0,
        internalNet: 0,
        countVisible: 0,
        countAll: 0,
      };
    }
    let bankDeposited = 0;
    let bankWithdrawn = 0;
    let internalIn = 0;
    let internalOut = 0;

    (depositsBaseRows || []).forEach(({ row, kind }) => {
      const amt = parseNumber(row?.amount) || 0;
      if (kind === 'bank_deposit') bankDeposited += Math.max(0, amt);
      if (kind === 'bank_withdrawal') bankWithdrawn += Math.abs(Math.min(0, amt));
      if (kind === 'internal_in') internalIn += Math.max(0, amt);
      if (kind === 'internal_out') internalOut += Math.abs(Math.min(0, amt));
    });
    const bankNet = bankDeposited - bankWithdrawn;
    const internalNet = internalIn - internalOut;
    return {
      bankDeposited,
      bankWithdrawn,
      bankNet,
      internalIn,
      internalOut,
      internalNet,
      countVisible: (depositsTableRows || []).length,
      countAll: (depositsBaseRows || []).length,
    };
  }, [activeSubmenu, depositsBaseRows, depositsTableRows]);

  const handleDepositsSort = (key) => {
    setDepositsSortConfig((prev) => {
      const nextDirection = prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc';
      return { key, direction: nextDirection };
    });
  };

  const getDepositsSortIcon = (key) => {
    if (depositsSortConfig.key !== key) return '⇅';
    return depositsSortConfig.direction === 'asc' ? '↑' : '↓';
  };

  const sortedDepositsRows = useMemo(() => {
    if (activeSubmenu !== 'deposits') return [];
    const rows = [...(depositsTableRows || [])];
    const key = depositsSortConfig?.key;
    if (!key) return rows;

    const getVal = ({ row, label, kind }) => {
      if (!row) return null;
      if (key === 'date') return row.date ? new Date(row.date).getTime() : null;
      if (key === 'account') return row.account_display_name || row.account_id || '';
      if (key === 'type') return label || kind || '';
      if (key === 'amount') return parseNumber(row.amount) ?? null;
      return row[key];
    };

    rows.sort((a, b) => {
      const av = getVal(a);
      const bv = getVal(b);
      if (av === null || av === undefined) return 1;
      if (bv === null || bv === undefined) return -1;
      if (typeof av === 'number' && typeof bv === 'number') {
        return depositsSortConfig.direction === 'asc' ? av - bv : bv - av;
      }
      return depositsSortConfig.direction === 'asc'
        ? String(av).localeCompare(String(bv))
        : String(bv).localeCompare(String(av));
    });

    return rows;
  }, [activeSubmenu, depositsTableRows, depositsSortConfig]);

  const depositsRowsWithRunningTotal = useMemo(() => {
    if (activeSubmenu !== 'deposits') return [];
    const out = [];
    let running = 0;
    sortedDepositsRows.forEach((item) => {
      const amt = parseNumber(item?.row?.amount) || 0;
      running += amt;
      out.push({ ...item, runningTotal: running });
    });
    return out;
  }, [activeSubmenu, sortedDepositsRows]);

  // Format date for display
  const formatDate = (date) => {
    if (!date) return '';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  // Get ISO week number
  const getISOWeek = (date) => {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
  };

  // Get week range (Monday to Sunday)
  const getWeekRange = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    // Calculate days to subtract to get to Monday (1 = Monday, 0 = Sunday)
    const daysToMonday = day === 0 ? 6 : day - 1;
    const monday = new Date(d);
    monday.setDate(d.getDate() - daysToMonday);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    return { start: monday, end: sunday };
  };

  // Get month range
  const getMonthRange = (date) => {
    const start = new Date(date.getFullYear(), date.getMonth(), 1);
    const end = new Date(date.getFullYear(), date.getMonth() + 1, 0);
    return { start, end };
  };

  // Group and aggregate data by selected period
  const groupedData = useMemo(() => {
    if (!equityData || equityData.length === 0) return [];

    const groups = new Map();

    equityData.forEach((item, index) => {
      const date = parseIBKRDate(item.report_date);
      if (!date) return;

      let groupKey;
      let dateRange;

      if (groupBy === 'day') {
        groupKey = date.toISOString().split('T')[0];
        dateRange = formatDate(date);
      } else if (groupBy === 'week') {
        const week = getWeekRange(date);
        // Use ISO week number for consistent grouping
        const weekNum = getISOWeek(week.start);
        groupKey = `${week.start.getFullYear()}-W${String(weekNum).padStart(2, '0')}`;
        dateRange = `${formatDate(week.start)} - ${formatDate(week.end)}`;
      } else if (groupBy === 'month') {
        const month = getMonthRange(date);
        groupKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        dateRange = `${formatDate(month.start)} - ${formatDate(month.end)}`;
      }

      if (!groups.has(groupKey)) {
        groups.set(groupKey, {
          dateRange,
          groupKey,
          cash: 0,
          stocks: 0,
          options: 0,
          total: 0,
          dates: [],
          firstDate: date,
          lastDate: date,
        });
      }

      const group = groups.get(groupKey);
      const cash = parseFloat(item.cash) || 0;
      const stocks = parseFloat(item.stock) || 0;
      const options = parseFloat(item.options) || 0;
      const total = parseFloat(item.total) || 0;

      // Use the latest values for each period (since we're sorted newest first)
      if (!group.dates.length || date > group.firstDate) {
        group.cash = cash;
        group.stocks = stocks;
        group.options = options;
        group.total = total;
        group.firstDate = date;
      }

      // Keep track of earliest date in group for comparison
      if (date < group.lastDate) {
        group.lastDate = date;
      }

      group.dates.push({ date, cash, stocks, options, total });
    });

    // Convert to array and calculate differences
    const result = Array.from(groups.values()).map((group, index, array) => {
      // Find previous period for comparison
      let previousGroup = null;
      if (index < array.length - 1) {
        // Since we're sorted newest first, previous is next in array
        previousGroup = array[index + 1];
      }

      const stocksDiff = previousGroup 
        ? group.stocks - previousGroup.stocks 
        : null;
      const optionsDiff = previousGroup 
        ? group.options - previousGroup.options 
        : null;
      const totalDiff = previousGroup 
        ? group.total - previousGroup.total 
        : null;

      return {
        ...group,
        stocksDiff,
        optionsDiff,
        totalDiff,
        sortDate: group.firstDate.getTime(),
      };
    });

    // Sort by date (newest first)
    return result.sort((a, b) => b.sortDate - a.sortDate);
  }, [equityData, groupBy]);

  // Calculate summary statistics
  const summary = useMemo(() => {
    if (groupedData.length === 0) {
      return {
        totalCash: 0,
        totalStocks: 0,
        totalOptions: 0,
        totalEquity: 0,
        totalDiff: 0,
        totalDiffPercent: 0,
        cashDiff: 0,
        cashDiffPercent: 0,
        stocksDiff: 0,
        stocksDiffPercent: 0,
        optionsDiff: 0,
        optionsDiffPercent: 0,
        lastUpdateDate: null,
      };
    }

    const latest = groupedData[0];
    const previous = groupedData.length > 1 ? groupedData[1] : null;

    const totalCash = latest.cash;
    const totalStocks = latest.stocks;
    const totalOptions = latest.options;
    const totalEquity = latest.total;
    const totalDiff = previous ? latest.total - previous.total : 0;
    const totalDiffPercent = previous && previous.total !== 0 
      ? ((totalDiff / previous.total) * 100) 
      : 0;

    const cashDiff = previous ? latest.cash - previous.cash : 0;
    const cashDiffPercent = previous && previous.cash !== 0
      ? ((cashDiff / previous.cash) * 100)
      : 0;

    const stocksDiff = previous ? latest.stocks - previous.stocks : 0;
    const stocksDiffPercent = previous && previous.stocks !== 0
      ? ((stocksDiff / previous.stocks) * 100)
      : 0;

    const optionsDiff = previous ? latest.options - previous.options : 0;
    const optionsDiffPercent = previous && previous.options !== 0
      ? ((optionsDiff / previous.options) * 100)
      : 0;

    return {
      totalCash,
      totalStocks,
      totalOptions,
      totalEquity,
      totalDiff,
      totalDiffPercent,
      cashDiff,
      cashDiffPercent,
      stocksDiff,
      stocksDiffPercent,
      optionsDiff,
      optionsDiffPercent,
      lastUpdateDate: latest.firstDate,
    };
  }, [groupedData]);

  // Handle column sorting
  const handleSort = (key) => {
    let direction = 'desc';
    if (sortConfig.key === key && sortConfig.direction === 'desc') {
      direction = 'asc';
    }
    setSortConfig({ key, direction });
  };

  // Sort grouped data
  const sortedData = useMemo(() => {
    if (!sortConfig.key) return groupedData;

    const sorted = [...groupedData].sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];

      if (sortConfig.key === 'dateRange') {
        aVal = a.sortDate;
        bVal = b.sortDate;
      }

      if (aVal === null || aVal === undefined) return 1;
      if (bVal === null || bVal === undefined) return -1;

      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
      }

      return sortConfig.direction === 'asc'
        ? String(aVal).localeCompare(String(bVal))
        : String(bVal).localeCompare(String(aVal));
    });

    return sorted;
  }, [groupedData, sortConfig]);

  // Format currency
  // Backend always returns EUR (TastyTrade USD is converted server-side)
  const currencySymbol = displayCurrency === 'USD' ? '$' : '€';
  const currencyMultiplier = displayCurrency === 'USD' && eurToUsdRate ? eurToUsdRate : 1;

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return '—';
    const num = parseFloat(value) * currencyMultiplier;
    if (isNaN(num)) return '—';
    return `${currencySymbol}${num.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const formatSignedCurrency = (value) => {
    if (value === null || value === undefined) return '—';
    const num = parseFloat(value) * currencyMultiplier;
    if (isNaN(num)) return '—';
    const abs = `${currencySymbol}${Math.abs(num).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    return num >= 0 ? `+${abs}` : `-${abs}`;
  };

  // Format difference with color
  const formatDiff = (value) => {
    if (value === null || value === undefined) return '—';
    const num = parseFloat(value) * currencyMultiplier;
    if (isNaN(num)) return '—';
    const formatted = `${currencySymbol}${Math.abs(num).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
    return num >= 0 ? `+${formatted}` : `-${formatted}`;
  };

  // Export to CSV
  const exportToCSV = () => {
    const headers = ['Period', 'Cash', 'Stocks', 'Options', 'Stocks Diff', 'Options Diff', 'Total', 'Difference'];
    const rows = sortedData.map(item => [
      item.dateRange,
      item.cash,
      item.stocks,
      item.options,
      item.stocksDiff !== null ? item.stocksDiff : '',
      item.optionsDiff !== null ? item.optionsDiff : '',
      item.total,
      item.totalDiff !== null ? item.totalDiff : '',
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `equity_${groupBy}_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return '⇅';
    return sortConfig.direction === 'asc' ? '↑' : '↓';
  };

  if (loading && activeSubmenu !== 'deposits') {
    return (
      <div className="equity-container">
        <div className="equity-loading">Loading equity data...</div>
      </div>
    );
  }

  if (error && activeSubmenu !== 'deposits') {
    return (
      <div className="equity-container">
        <div className="equity-error">{error}</div>
      </div>
    );
  }

  // Render different views based on activeSubmenu
  if (activeSubmenu === 'goals') {
    if (loading) {
      return (
        <div className="equity-container">
          <div className="equity-header">
            <h1>Equity Dashboard - Goals</h1>
          </div>
          <div className="equity-loading">Loading goals data...</div>
        </div>
      );
    }
    
    if (error) {
      return (
        <div className="equity-container">
          <div className="equity-header">
            <h1>Equity Dashboard - Goals</h1>
          </div>
          <div className="equity-error">{error}</div>
        </div>
      );
    }
    
    return (
      <div className="equity-container">
        <div className="equity-header">
          <h1>Equity Dashboard - Goals</h1>
        </div>
        <GoalsDisplay 
          equityData={equityData}
          userSettings={userSettings}
          mode={pnlMode}
          onModeChange={handlePnLModeChange}
          selectedAccount={selectedAccount}
          accounts={accounts}
          onRefresh={() => {
            fetchUserSettings();
            fetchEquityData();
          }}
        />
      </div>
    );
  }

  if (activeSubmenu === 'deposits') {
    if (depositsLoading) {
      return (
        <div className="equity-container">
          <div className="equity-header">
            <h1>Equity Dashboard - Deposits/Withdrawals</h1>
          </div>
          <div className="equity-loading">Loading deposits/withdrawals...</div>
        </div>
      );
    }

    if (depositsError) {
      return (
        <div className="equity-container">
          <div className="equity-header">
            <h1>Equity Dashboard - Deposits/Withdrawals</h1>
          </div>
          <div className="equity-error">{depositsError}</div>
        </div>
      );
    }

    return (
      <div className="equity-container">
        <div className="equity-header">
          <h1>Equity Dashboard - Deposits/Withdrawals</h1>
          <div className="equity-controls">
            <div className="equity-control-group">
              <label htmlFor="deposits-filter-date">Date:</label>
              <input
                id="deposits-filter-date"
                type="date"
                className="equity-select"
                value={depositsFilterDate}
                onChange={(e) => setDepositsFilterDate(e.target.value)}
              />
              {depositsFilterDate ? (
                <button
                  type="button"
                  className="equity-select"
                  style={{ minWidth: 'auto', padding: '0.5rem 0.65rem' }}
                  onClick={() => setDepositsFilterDate('')}
                  aria-label="Clear date filter"
                >
                  Clear
                </button>
              ) : null}
            </div>
            <div className="equity-control-group">
              <label htmlFor="deposits-filter-account">Account:</label>
              <select
                id="deposits-filter-account"
                value={selectedAccount}
                onChange={(e) => setSelectedAccount(e.target.value)}
                className="equity-select"
              >
                <option value="all">All Accounts</option>
                {accounts.map((acc) => (
                  <option key={acc.id} value={acc.id}>
                    {acc.display_name || acc.broker_account_id}
                  </option>
                ))}
              </select>
            </div>
            <div className="equity-control-group">
              <label htmlFor="deposits-filter-type">Type:</label>
              <select
                id="deposits-filter-type"
                value={depositsFilterType}
                onChange={(e) => setDepositsFilterType(e.target.value)}
                className="equity-select"
              >
                <option value="all">All types</option>
                <option value="bank_deposit">Deposited from bank</option>
                <option value="bank_withdrawal">Withdrawn to bank</option>
                <option value="internal">Internal transfer</option>
              </select>
            </div>
            <div className="equity-control-group">
              <label>
                <input
                  type="checkbox"
                  checked={depositsHideInternal}
                  onChange={(e) => setDepositsHideInternal(e.target.checked)}
                />
                Hide internal transfers
              </label>
            </div>
            <div className="equity-control-group">
              <label>Currency:</label>
              <div className="equity-toggle-group">
                <button
                  className={displayCurrency === 'EUR' ? 'active' : ''}
                  onClick={() => setDisplayCurrency('EUR')}
                >
                  € EUR
                </button>
                <button
                  className={displayCurrency === 'USD' ? 'active' : ''}
                  onClick={() => setDisplayCurrency('USD')}
                  disabled={!eurToUsdRate}
                  title={eurToUsdRate ? `Rate: 1 EUR = ${eurToUsdRate.toFixed(4)} USD` : 'FX rate unavailable'}
                >
                  $ USD
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="equity-summary-cards">
          <div className="equity-card" title={`Bank-only totals (rows in table: ${depositsSummary.countVisible}, rows after date/account filter: ${depositsSummary.countAll})`}>
            <div className="equity-card-label">Deposited from bank</div>
            <div className="equity-card-value">{formatCurrency(depositsSummary.bankDeposited)}</div>
          </div>
          <div className="equity-card" title={`Bank-only totals (rows in table: ${depositsSummary.countVisible}, rows after date/account filter: ${depositsSummary.countAll})`}>
            <div className="equity-card-label">Withdrawn to bank</div>
            <div className="equity-card-value">{formatCurrency(depositsSummary.bankWithdrawn)}</div>
          </div>
          <div className={`equity-card ${depositsSummary.bankNet >= 0 ? 'positive' : 'negative'}`} title={`Bank-only net`}>
            <div className="equity-card-label">Net (bank)</div>
            <div className="equity-card-value">{formatSignedCurrency(depositsSummary.bankNet)}</div>
          </div>
          {!depositsHideInternal ? (
            <div className={`equity-card ${depositsSummary.internalNet >= 0 ? 'positive' : 'negative'}`} title="Internal transfers (should net ~0 over time)">
              <div className="equity-card-label">Net (internal)</div>
              <div className="equity-card-value">{formatSignedCurrency(depositsSummary.internalNet)}</div>
            </div>
          ) : null}
        </div>

        <div className="equity-table-container">
          <table className="equity-table">
            <thead>
              <tr>
                <th onClick={() => handleDepositsSort('date')}>
                  Date
                  <span className="equity-sort-icon">{getDepositsSortIcon('date')}</span>
                </th>
                <th onClick={() => handleDepositsSort('account')}>
                  Account
                  <span className="equity-sort-icon">{getDepositsSortIcon('account')}</span>
                </th>
                <th onClick={() => handleDepositsSort('type')}>
                  Type
                  <span className="equity-sort-icon">{getDepositsSortIcon('type')}</span>
                </th>
                <th onClick={() => handleDepositsSort('amount')}>
                  Amount
                  <span className="equity-sort-icon">{getDepositsSortIcon('amount')}</span>
                </th>
                <th onClick={() => handleDepositsSort('runningTotal')}>
                  Total
                  <span className="equity-sort-icon">{getDepositsSortIcon('runningTotal')}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {depositsRowsWithRunningTotal.map(({ row, label, runningTotal }) => {
                const amt = parseNumber(row?.amount);
                const runningClass = runningTotal >= 0 ? 'positive' : 'negative';
                return (
                <tr key={row.id}>
                  <td title={row.date}>
                    {formatDutchDate(row.date)}
                  </td>
                  <td>{row.account_display_name || row.account_id || '—'}</td>
                  <td>{label}</td>
                  <td
                    style={{ fontWeight: 600 }}
                    className={amt !== null ? (amt >= 0 ? 'positive' : 'negative') : ''}
                    title={amt !== null ? formatSignedCurrency(amt) : '—'}
                  >
                    {amt !== null ? formatSignedCurrency(amt) : '—'}
                  </td>
                  <td className={runningClass} title={formatSignedCurrency(runningTotal)}>
                    {formatSignedCurrency(runningTotal)}
                  </td>
                </tr>
              );
              })}
              {depositsRowsWithRunningTotal.length === 0 ? (
                <tr>
                  <td colSpan={8} style={{ textAlign: 'center', padding: '24px', color: '#666' }}>
                    No deposits/withdrawals match the current filters.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  return (
    <div className="equity-container">
      <div className="equity-header">
        <h1>Equity Dashboard</h1>
        <div className="equity-controls">
          <div className="equity-control-group">
            <label>Broker:</label>
            <select
              value={selectedBroker}
              onChange={(e) => { setSelectedBroker(e.target.value); setSelectedAccount('all'); }}
              className="equity-select"
            >
              <option value="all">All brokers</option>
              <option value="ibkr">IBKR</option>
              <option value="tastytrade">TastyTrade</option>
            </select>
          </div>
          <div className="equity-control-group">
            <label>Account:</label>
            <select
              value={selectedAccount}
              onChange={(e) => setSelectedAccount(e.target.value)}
              className="equity-select"
            >
              <option value="all">All Accounts</option>
              {accounts
                .filter(acc => selectedBroker === 'all' || acc.broker === selectedBroker)
                .map(acc => (
                  <option key={acc.id} value={acc.id}>
                    {acc.display_name || acc.broker_account_id}
                  </option>
                ))}
            </select>
          </div>
          <div className="equity-control-group">
            <label>Group By:</label>
            <div className="equity-toggle-group">
              <button
                className={groupBy === 'day' ? 'active' : ''}
                onClick={() => setGroupBy('day')}
              >
                Day
              </button>
              <button
                className={groupBy === 'week' ? 'active' : ''}
                onClick={() => setGroupBy('week')}
              >
                Week
              </button>
              <button
                className={groupBy === 'month' ? 'active' : ''}
                onClick={() => setGroupBy('month')}
              >
                Month
              </button>
            </div>
          </div>
          <div className="equity-control-group">
            <label>
              <input
                type="checkbox"
                checked={showDiffColumns}
                onChange={(e) => setShowDiffColumns(e.target.checked)}
              />
              Show Differences
            </label>
          </div>
          <button onClick={exportToCSV} className="equity-export-btn">
            Export CSV
          </button>
          <button
            className={`equity-currency-toggle${displayCurrency === 'USD' ? ' active' : ''}`}
            onClick={() => setDisplayCurrency(c => c === 'EUR' ? 'USD' : 'EUR')}
            disabled={!eurToUsdRate}
            title={eurToUsdRate ? `Rate: 1 EUR = ${eurToUsdRate.toFixed(4)} USD` : 'FX rate unavailable'}
          >
            {displayCurrency === 'EUR' ? '€ → $' : '$ → €'}
          </button>
        </div>
      </div>

      <div className="equity-summary-cards">
        <div className="equity-card" title={`Last updated: ${summary.lastUpdateDate ? formatDate(summary.lastUpdateDate) : 'N/A'}`}>
          <div className="equity-card-label">Total Cash</div>
          <div className="equity-card-value">
            {formatCurrency(summary.totalCash)}
            {summary.cashDiff !== 0 && (
              <span className={`equity-card-change ${summary.cashDiff >= 0 ? 'positive' : 'negative'}`}>
                <span className="equity-card-arrow">{summary.cashDiff >= 0 ? '↑' : '↓'}</span>
                {formatDiff(summary.cashDiff)}
                {summary.cashDiffPercent !== 0 && (
                  <span className="equity-card-percent">
                    {' '}({summary.cashDiffPercent >= 0 ? '+' : ''}{summary.cashDiffPercent.toFixed(2)}%)
                  </span>
                )}
              </span>
            )}
          </div>
        </div>
        <div className="equity-card" title={`Last updated: ${summary.lastUpdateDate ? formatDate(summary.lastUpdateDate) : 'N/A'}`}>
          <div className="equity-card-label">Total Stocks</div>
          <div className="equity-card-value">
            {formatCurrency(summary.totalStocks)}
            {summary.stocksDiff !== 0 && (
              <span className={`equity-card-change ${summary.stocksDiff >= 0 ? 'positive' : 'negative'}`}>
                <span className="equity-card-arrow">{summary.stocksDiff >= 0 ? '↑' : '↓'}</span>
                {formatDiff(summary.stocksDiff)}
                {summary.stocksDiffPercent !== 0 && (
                  <span className="equity-card-percent">
                    {' '}({summary.stocksDiffPercent >= 0 ? '+' : ''}{summary.stocksDiffPercent.toFixed(2)}%)
                  </span>
                )}
              </span>
            )}
          </div>
        </div>
        <div className="equity-card" title={`Last updated: ${summary.lastUpdateDate ? formatDate(summary.lastUpdateDate) : 'N/A'}`}>
          <div className="equity-card-label">Total Options</div>
          <div className="equity-card-value">
            {formatCurrency(summary.totalOptions)}
            {summary.optionsDiff !== 0 && (
              <span className={`equity-card-change ${summary.optionsDiff >= 0 ? 'positive' : 'negative'}`}>
                <span className="equity-card-arrow">{summary.optionsDiff >= 0 ? '↑' : '↓'}</span>
                {formatDiff(summary.optionsDiff)}
                {summary.optionsDiffPercent !== 0 && (
                  <span className="equity-card-percent">
                    {' '}({summary.optionsDiffPercent >= 0 ? '+' : ''}{summary.optionsDiffPercent.toFixed(2)}%)
                  </span>
                )}
              </span>
            )}
          </div>
        </div>
        <div className="equity-card" title={`Last updated: ${summary.lastUpdateDate ? formatDate(summary.lastUpdateDate) : 'N/A'}`}>
          <div className="equity-card-label">Total Equity</div>
          <div className="equity-card-value">{formatCurrency(summary.totalEquity)}</div>
        </div>
        <div className={`equity-card ${summary.totalDiff >= 0 ? 'positive' : 'negative'}`} title={`Last updated: ${summary.lastUpdateDate ? formatDate(summary.lastUpdateDate) : 'N/A'}`}>
          <div className="equity-card-label">Total Difference</div>
          <div className="equity-card-value">
            <span>
              <span className="equity-card-arrow">{summary.totalDiff >= 0 ? '↑' : '↓'}</span>
              {formatDiff(summary.totalDiff)}
            </span>
            {summary.totalDiffPercent !== 0 && (
              <span className="equity-card-percent">
                ({summary.totalDiffPercent >= 0 ? '+' : ''}{summary.totalDiffPercent.toFixed(2)}%)
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="equity-table-container">
        <table className="equity-table">
          <thead>
            <tr>
              <th 
                className="equity-sticky-col"
                onClick={() => handleSort('dateRange')}
              >
                {groupBy === 'day' ? 'Day' : groupBy === 'week' ? 'Week' : 'Month'}
                <span className="equity-sort-icon">{getSortIcon('dateRange')}</span>
              </th>
              <th onClick={() => handleSort('cash')}>
                Cash
                <span className="equity-sort-icon">{getSortIcon('cash')}</span>
              </th>
              <th onClick={() => handleSort('stocks')}>
                Stocks
                <span className="equity-sort-icon">{getSortIcon('stocks')}</span>
              </th>
              {showDiffColumns && (
                <th onClick={() => handleSort('stocksDiff')}>
                  Stocks Diff
                  <span className="equity-sort-icon">{getSortIcon('stocksDiff')}</span>
                </th>
              )}
              <th onClick={() => handleSort('options')}>
                Options
                <span className="equity-sort-icon">{getSortIcon('options')}</span>
              </th>
              {showDiffColumns && (
                <th onClick={() => handleSort('optionsDiff')}>
                  Options Diff
                  <span className="equity-sort-icon">{getSortIcon('optionsDiff')}</span>
                </th>
              )}
              <th onClick={() => handleSort('total')}>
                Total
                <span className="equity-sort-icon">{getSortIcon('total')}</span>
              </th>
              <th onClick={() => handleSort('totalDiff')}>
                Difference
                <span className="equity-sort-icon">{getSortIcon('totalDiff')}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedData.length === 0 ? (
              <tr>
                <td colSpan={showDiffColumns ? 8 : 6} className="equity-empty">
                  No data available
                </td>
              </tr>
            ) : (
              sortedData.map((row, index) => {
                const previousRow = index < sortedData.length - 1 ? sortedData[index + 1] : null;
                const cashDiffPercent = previousRow && previousRow.cash !== 0
                  ? ((row.cash - previousRow.cash) / previousRow.cash) * 100
                  : null;
                const stocksDiffPercent = previousRow && previousRow.stocks !== 0
                  ? ((row.stocks - previousRow.stocks) / previousRow.stocks) * 100
                  : null;
                const optionsDiffPercent = previousRow && previousRow.options !== 0
                  ? ((row.options - previousRow.options) / previousRow.options) * 100
                  : null;
                const totalDiffPercent = previousRow && previousRow.total !== 0
                  ? ((row.total - previousRow.total) / previousRow.total) * 100
                  : null;

                return (
                  <tr key={row.groupKey}>
                    <td className="equity-sticky-col" title={`Period: ${row.dateRange}`}>{row.dateRange}</td>
                    <td 
                      title={`Cash: ${formatCurrency(row.cash)}${cashDiffPercent !== null ? `\nChange: ${cashDiffPercent >= 0 ? '+' : ''}${cashDiffPercent.toFixed(2)}%` : ''}\nLast update: ${formatDate(row.firstDate)}`}
                    >
                      {formatCurrency(row.cash)}
                    </td>
                    <td 
                      title={`Stocks: ${formatCurrency(row.stocks)}${stocksDiffPercent !== null ? `\nChange: ${stocksDiffPercent >= 0 ? '+' : ''}${stocksDiffPercent.toFixed(2)}%` : ''}\nLast update: ${formatDate(row.firstDate)}`}
                    >
                      {formatCurrency(row.stocks)}
                    </td>
                    {showDiffColumns && (
                      <td 
                        className={row.stocksDiff !== null && row.stocksDiff >= 0 ? 'positive' : row.stocksDiff !== null ? 'negative' : ''}
                        title={`Stocks difference: ${formatDiff(row.stocksDiff)}${stocksDiffPercent !== null ? `\nPercentage: ${stocksDiffPercent >= 0 ? '+' : ''}${stocksDiffPercent.toFixed(2)}%` : ''}`}
                      >
                        {formatDiff(row.stocksDiff)}
                      </td>
                    )}
                    <td 
                      title={`Options: ${formatCurrency(row.options)}${optionsDiffPercent !== null ? `\nChange: ${optionsDiffPercent >= 0 ? '+' : ''}${optionsDiffPercent.toFixed(2)}%` : ''}\nLast update: ${formatDate(row.firstDate)}`}
                    >
                      {formatCurrency(row.options)}
                    </td>
                    {showDiffColumns && (
                      <td 
                        className={row.optionsDiff !== null && row.optionsDiff >= 0 ? 'positive' : row.optionsDiff !== null ? 'negative' : ''}
                        title={`Options difference: ${formatDiff(row.optionsDiff)}${optionsDiffPercent !== null ? `\nPercentage: ${optionsDiffPercent >= 0 ? '+' : ''}${optionsDiffPercent.toFixed(2)}%` : ''}`}
                      >
                        {formatDiff(row.optionsDiff)}
                      </td>
                    )}
                    <td 
                      title={`Total Equity: ${formatCurrency(row.total)}\nLast update: ${formatDate(row.firstDate)}`}
                    >
                      {formatCurrency(row.total)}
                    </td>
                    <td 
                      className={row.totalDiff !== null && row.totalDiff >= 0 ? 'positive' : row.totalDiff !== null ? 'negative' : ''}
                      title={`Total difference: ${formatDiff(row.totalDiff)}${totalDiffPercent !== null ? `\nPercentage: ${totalDiffPercent >= 0 ? '+' : ''}${totalDiffPercent.toFixed(2)}%` : ''}`}
                    >
                      {formatDiff(row.totalDiff)}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default Equity;
